import { useState, useRef, useEffect } from 'react';
import { formatDate, FORMAT } from '../../../../lib/date';
import { isNumberOrEmpty, convertWeight } from '../../../../lib/utils';
import { WEIGHT_UNIT } from '../../../../types/enum';
import Title from '../../../../components/dialog/title';
import AddExercise from './addExercise';
import ExerciseList from './exercise';
import ExtraDetail from './extraDetail';
import request from '../../../../lib/request';

const WorkoutDateAndDuration = ({
  selectedMonth,
  handleChangeDate,
  duration,
  handleChangeDuration,
}) => {
  return (
    <div className='flex flex-col gap-4 mb-4'>
      <div className='flex items-center justify-between gap-2'>
        <label className='text-white font-semibold'>Date</label>
        <input
          type='datetime-local'
          max={formatDate(new Date(), FORMAT.DATEHOURMIN)}
          value={selectedMonth}
          onChange={handleChangeDate}
          className='rounded-full px-2 py-1 bg-tertiary  text-white'
        />
      </div>
      <div className='flex items-center justify-between gap-2'>
        <label className='text-white font-semibold'>Duration</label>
        <div className='text-white flex items-center gap-2'>
          <div className='flex items-center gap-2'>
            <select
              name='hours'
              value={duration.hours}
              onChange={handleChangeDuration}
              className='rounded-full px-2 py-1 bg-tertiary  text-white max-w-16 text-center'
            >
              {Array.from({ length: 25 }, (_, i) => (
                <option
                  key={i}
                  value={i}
                >
                  {i}
                </option>
              ))}
            </select>
            <span>h</span>
          </div>
          <div className='flex items-center gap-2'>
            <select
              name='minutes'
              value={duration.minutes}
              onChange={handleChangeDuration}
              className='rounded-full px-2 py-1 bg-tertiary  text-white max-w-16 text-center'
            >
              {Array.from({ length: 60 }, (_, i) => (
                <option
                  key={i}
                  value={i}
                >
                  {i}
                </option>
              ))}
            </select>
            <span>min</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const WorkoutFeelingAndNote = ({
  note,
  handleChangeNote,
  feeling,
  handleSelectFeeling,
  level,
  handleChangeLevel,
  user,
}) => {
  const [emojiExpression, setEmojiExpression] = useState([]);

  useEffect(() => {
    getEmojiExpression();
  }, []);

  const getEmojiExpression = async () => {
    try {
      const { result } = await request.get('/static/emoji-expression');
      setEmojiExpression(result);
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <div className='mb-4'>
      {user.programRegistered && (
        <div className='flex items-center justify-between gap-2 mb-4'>
          <label className='text-white font-semibold'>Level</label>
          <select
            name='level'
            value={level}
            onChange={handleChangeLevel}
            className='rounded-full px-2 py-1 bg-tertiary  text-white text-center'
          >
            {Array.from({ length: 5 }, (_, i) => (
              <option
                key={i}
                value={i + 1}
              >
                {i + 1}
                {i + 1 === 1 ? ' (easy)' : i + 1 === 5 ? ' (hard)' : ''}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className='text-white font-semibold'>
        <span className='mr-2'>How was today's workout?</span>
        <span className='text-xl space-x-1'>
          {Array.from({ length: 3 }, (_, i) => (
            <span key={i}>{feeling[i] ? feeling[i] : '🫥'}</span>
          ))}
        </span>
      </div>
      <div className='text-xs text-white/50 mb-1'>Select 1-3 best feelings</div>
      <div className='flex items-center gap-4 py-2 overflow-x-scroll scrollbar-hidden mb-4'>
        {emojiExpression.map((item) => (
          <button
            key={item.emoji}
            className={`flex-shrink-0 size-[72px] flex flex-col items-center justify-center gap-1 rounded-xl p-1 ${
              feeling.includes(item.emoji)
                ? 'bg-secondary border-2 border-accent'
                : 'bg-tertiary'
            }`}
            onClick={() => handleSelectFeeling(item.emoji)}
          >
            <span className='text-xl'>{item.emoji}</span>
            <span className='text-[10px] text-white/70 leading-none'>
              {item.meaning}
            </span>
          </button>
        ))}
      </div>
      <div className='flex flex-col gap-2'>
        <label className='text-white font-semibold'>Note</label>
        <textarea
          value={note ?? ''}
          rows={2}
          onChange={handleChangeNote}
          className='rounded-xl px-3 py-2 bg-tertiary  text-white resize-none'
        />
      </div>
    </div>
  );
};

const Detail = ({
  selectedLog,
  closeDialog,
  refreshWorkoutTracker,
  preference,
  user,
}) => {
  const addExerciseRef = useRef(null);

  const [selectedMonth, setSelectedMonth] = useState('');
  const [duration, setDuration] = useState({
    hours: 0,
    minutes: 0,
  });
  const [feeling, setFeeling] = useState([]);
  const [note, setNote] = useState('');
  const [extra, setExtra] = useState({
    distance: 0,
    steps: 0,
    cycle: 0,
    restCycle: 0,
    restExercise: 0,
  });
  const [selectedExercises, setSelectedExercises] = useState([]);
  const [level, setLevel] = useState(1);

  useEffect(() => {
    if (selectedLog) {
      setSelectedMonth(`${selectedLog.logDate}T${selectedLog.logTime}`);
      setDuration({
        hours: selectedLog.duration
          ? Math.floor(selectedLog.duration / 3600)
          : 0,
        minutes: selectedLog.duration
          ? Math.floor((selectedLog.duration % 3600) / 60)
          : 0,
      });
      setFeeling(selectedLog.feeling ? selectedLog.feeling.split(',') : []);
      setNote(selectedLog.note);
      setExtra({
        distance: selectedLog.distance ? selectedLog.distance : 0,
        steps: selectedLog.steps ? selectedLog.steps : 0,
        cycle: selectedLog.cycle ? selectedLog.cycle : 0,
        restCycle: selectedLog.restCycle ? selectedLog.restCycle : 0,
        restExercise: selectedLog.restExercise ? selectedLog.restExercise : 0,
      });
      setLevel(selectedLog.level ? selectedLog.level : 1);
      if (selectedLog.logs.length > 0) {
        const logList = [];

        selectedLog.logs.reduce((acc, cur, i, arr) => {
          if (i === 0) {
            acc = { ...cur };

            if (acc.groupId) {
              acc.sets = acc.sets.map((s) => [s]);
            }

            if (i === arr.length - 1) {
              logList.push(acc);
              acc = {};
            }
            return acc;
          }
          if (acc.groupId && acc.groupId === cur.groupId) {
            acc.name = Array.isArray(acc.name)
              ? [...acc.name, cur.name]
              : [acc.name, cur.name];
            acc.sets = acc.sets.map((s, i) => [...s, cur.sets[i]]);
          } else {
            logList.push(acc);
            acc = { ...cur };
          }

          if (i === arr.length - 1) {
            logList.push(acc);
          }
          return acc;
        }, {});
        setSelectedExercises(logList);
      }
    }
  }, [selectedLog]);

  const handleChangeDate = (e) => {
    setSelectedMonth(e.target.value);
  };

  const handleChangeDuration = (e) => {
    if (isNumberOrEmpty(e.target.value)) {
      setDuration({
        ...duration,
        [e.target.name]: e.target.value,
      });
    }
  };

  const handleChangeNote = (e) => {
    setNote(e.target.value);
  };

  const handleSelectFeeling = (emoji) => {
    if (feeling.includes(emoji)) {
      setFeeling(feeling.filter((item) => item !== emoji));
    } else if (feeling.length < 3) {
      setFeeling([...feeling, emoji]);
    } else {
      alert('You selected 3 best feelings.');
    }
  };

  const handleChangeLevel = (e) => {
    setLevel(e.target.value);
  };

  const handleSubmit = async () => {
    try {
      const dateArr = selectedMonth.split('T');
      const hours = Number(duration.hours) * 60;
      const minutes = Number(duration.minutes);
      const total = hours + minutes;

      const body = {
        logDate: dateArr[0],
        logTime: dateArr[1],
        duration: total * 60,
        note,
        logs: [],
        feeling: feeling.join(','),
        level: Number(level),
        workoutAt: new Date(selectedMonth).toISOString(),
      };

      if (selectedLog.type === 'CARDIO') {
        body.distance = Number(extra.distance);
        body.steps = Number(extra.steps);
      }
      if (selectedLog.type === 'HIIT') {
        body.cycle = Number(extra.cycle);
        body.restCycle = Number(extra.restCycle);
        body.restExercise = Number(extra.restExercise);
      }

      selectedExercises.forEach((exercise) => {
        if (!exercise.groupId) {
          exercise.sets = exercise.sets.map((set) => ({
            unit: set.unit.toLowerCase() === 'reps' ? 'reps' : 'sec',
            weight:
              preference.weightUnit === WEIGHT_UNIT.LB
                ? convertWeight(
                    Number(set.weight),
                    WEIGHT_UNIT.LB,
                    WEIGHT_UNIT.KG
                  )
                : Number(set.weight),
            count: Number(set.count),
          }));
          body.logs.push(exercise);
        } else {
          const eachExercise = exercise.name.map((n, i) => {
            return {
              name: n,
              groupId: exercise.groupId,
              order: i + 1,
              type: exercise.type,
              sets: exercise.sets.map((set) => ({
                unit: set[i].unit.toLowerCase() === 'reps' ? 'reps' : 'sec',
                weight:
                  preference.weightUnit === WEIGHT_UNIT.LB
                    ? convertWeight(
                        Number(set[i].weight),
                        WEIGHT_UNIT.LB,
                        WEIGHT_UNIT.KG
                      )
                    : Number(set[i].weight),
                count: Number(set[i].count),
              })),
            };
          });
          body.logs.push(...eachExercise);
        }
      });

      const { result } = await request.put(
        `/workout/record/${selectedLog.id}`,
        body
      );
      if (result) {
        await refreshWorkoutTracker();
        handleCloseDetailDialog();
      }
    } catch (err) {
      console.log(err);
    }
  };

  const openAddExerciseDialog = () => {
    addExerciseRef.current.showModal();
  };

  const closeAddExerciseDialog = () => {
    addExerciseRef.current.close();
  };

  const handleCloseDetailDialog = () => {
    setSelectedExercises([]);
    setFeeling([]);
    setNote('');
    setExtra({
      distance: 0,
      steps: 0,
      cycle: 0,
      restCycle: 0,
      restExercise: 0,
    });
    setDuration({
      hours: 0,
      minutes: 0,
    });
    closeDialog();
  };

  const handleSetSelectedExercises = (exercises) => {
    setSelectedExercises([...selectedExercises, ...exercises]);
  };

  const handleDeleteExercise = (index) => {
    if (window.confirm('Are you sure you want to delete this exercise?')) {
      setSelectedExercises(selectedExercises.filter((_, i) => i !== index));
    }
  };

  return (
    <div>
      <div className='flex items-center justify-between'>
        <Title
          title={selectedLog?.workout}
          onClick={handleCloseDetailDialog}
          color='white'
        />
      </div>

      <WorkoutDateAndDuration
        selectedMonth={selectedMonth}
        handleChangeDate={handleChangeDate}
        duration={duration}
        handleChangeDuration={handleChangeDuration}
      />

      <ExtraDetail
        type={selectedLog?.type}
        extra={extra}
        setExtra={setExtra}
      />

      {!['CARDIO', 'STRETCHING', 'SPORTS'].includes(selectedLog?.type) && (
        <div className='mb-4'>
          <button
            onClick={openAddExerciseDialog}
            className='flex items-center justify-center text-white bg-secondary rounded-xl w-full h-12 gap-2'
          >
            <span>Add Exercise</span>
            <span className='text-2xl'>+</span>
          </button>
        </div>
      )}

      <ExerciseList
        selectedExercises={selectedExercises}
        setSelectedExercises={setSelectedExercises}
        handleDeleteExercise={handleDeleteExercise}
        preference={preference}
      />

      <WorkoutFeelingAndNote
        note={note}
        feeling={feeling}
        handleSelectFeeling={handleSelectFeeling}
        handleChangeNote={handleChangeNote}
        level={level}
        handleChangeLevel={handleChangeLevel}
        user={user}
      />

      <AddExercise
        dialogRef={addExerciseRef}
        closeDialog={closeAddExerciseDialog}
        handleSetSelectedExercises={handleSetSelectedExercises}
      />

      <button
        onClick={handleSubmit}
        className='rounded-full w-full py-2 bg-accent font-bold'
      >
        Save
      </button>
    </div>
  );
};

export default Detail;
